/**
 * Avatar: a person's photo, or their initials over a stable color.
 *
 * It's the ONLY place in the app that renders an avatar. There used to be
 * five, each with its own initials rule (taking the first character with
 * different criteria) and all with the same color for everyone.
 *
 * Decisions that aren't obvious from reading the JSX:
 *
 *   · `onError` falls back to initials. A photo deleted from the bucket (or
 *     a row pointing to an object that no longer exists) should look like
 *     "no photo", not like a broken icon.
 *   · `alt=""` by default: on almost every screen the name is written right
 *     next to it, so announcing it again is noise for a screen reader. When
 *     the avatar goes ALONE, an explicit `alt` is passed.
 *   · The color comes from `userId`, not from the name — see
 *     `avatarPaletteIndex`.
 */
import { type Component, createMemo, createSignal, Show } from "solid-js";
import { avatarPaletteIndex, avatarUrl, cn, initialsOf } from "@space/shared";
import { SERVER_URL } from "@/lib/runtime-config";

/**
 * Default API origin.
 *
 * The component resolves it on its own so that the ~5 places that render an
 * avatar don't have to remember to pass it (and so none of them gets the
 * origin wrong). `apiBaseUrl` still exists as a prop for tests and for
 * multi-origin scenarios.
 */
const apiBaseUrl = () => SERVER_URL;

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

/** Diameter and font size per size. */
const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-24 w-24 text-2xl",
};

export interface AvatarProps {
  /** Person's id: this is where the COLOR comes from (stable even if the name changes). */
  userId?: string | null;
  /** Display name; this is where the initials come from. */
  name?: string | null;
  /** Fallback email when there's no name. */
  email?: string | null;
  /** Storage key (`avatars/…`) or absolute URL. Without this, initials. */
  image?: string | null;
  /** API origin used to build the URL from the key. */
  apiBaseUrl?: string | null;
  size?: AvatarSize;
  /** Alt text. Empty = decorative (the name is already right next to it). */
  alt?: string;
  class?: string;
  /** Container's `data-testid`. */
  testId?: string;
}

export const Avatar: Component<AvatarProps> = (props) => {
  // The URL that failed is stored, not a boolean: this way, when the user
  // changes their photo, the error state doesn't stay stuck on the previous
  // image (which is what a `failed: boolean` with no reset effect would do).
  const [failedSrc, setFailedSrc] = createSignal<string | null>(null);

  const size = () => props.size ?? "md";
  const src = createMemo(() => avatarUrl(props.image, props.apiBaseUrl ?? apiBaseUrl()));
  const initials = createMemo(() => initialsOf(props.name, props.email));
  const tone = createMemo(() => avatarPaletteIndex(props.userId));
  const usableSrc = createMemo(() => {
    const url = src();
    return url && url !== failedSrc() ? url : null;
  });

  return (
    <div
      class={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold select-none",
        SIZE_CLASSES[size()],
        // The tone only applies when initials are rendered; behind an opaque
        // photo it would be invisible, but it's kept so the placeholder has
        // color while the image loads.
        "avatar-tone",
        `avatar-tone-${tone()}`,
        props.class,
      )}
      data-testid={props.testId}
      data-avatar-tone={tone()}
    >
      <Show when={usableSrc()} fallback={<span aria-hidden="true">{initials()}</span>}>
        {(url) => (
          <img
            src={url()}
            alt={props.alt ?? ""}
            class="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
            onError={() => setFailedSrc(url())}
            data-testid={props.testId ? `${props.testId}-image` : undefined}
          />
        )}
      </Show>
    </div>
  );
};
